import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { type CampaignView, type CampaignDetailsState } from "../../types/state";
import { PriorityLevels, ReducerEntityPrefixTypes, ReducerNamespaceTypes } from "enums";
import { type CampaignOption, type CampaignFormFields } from "../../Details/types";

const initialState: CampaignDetailsState = {
  campaign: {
    id: -1,
    ownerId: -1,
    name: "",
    campaignTypeId: undefined,
    subDomainId: undefined,
    isDraft: true,
    isPurchased: false,
    hasBeenPublished: false,
    dateModified: undefined,
    labels: [],
    remedialTraining: {
      priority: PriorityLevels.none,
      daysToComplete: 1,
      dueDate: "",
      triggerTypeId: undefined,
      flowId: undefined,
      flowTitle: "",
      flowThumbnailUrl: "",
    },
  },
  campaignTypes: [],
  triggerTypes: [],
  subdomains: [],
  isLoaded: false,
  isLoading: false,
  isLoadingOptions: false,
};

const campaignDetailsSlice = createSlice({
  name: `${ReducerNamespaceTypes.Library}/${ReducerEntityPrefixTypes.ThreatDefenceCampaigns}/campaign`,
  initialState,
  reducers: {
    saveCampaign(state: CampaignDetailsState, action: PayloadAction<CampaignFormFields>) {
      state.campaign.name = action.payload.name;
      state.campaign.campaignTypeId = action.payload.campaignTypeId;
      state.campaign.subDomainId = action.payload.subDomainId;
      state.campaign.remedialTraining = {
        triggerTypeId: action.payload.remedialTraining.triggerTypeId,
        priority: action.payload.remedialTraining.priority,
        daysToComplete: action.payload.remedialTraining.daysToComplete,
        dueDate: action.payload.remedialTraining.fixedDueDate,
        flowId: action.payload.remedialTraining.flowId,
        flowTitle: action.payload.remedialTraining.flowTitle,
        flowThumbnailUrl: action.payload.remedialTraining.thumbnailUrl,
      };
    },
    clearCampaign: () => initialState,
    reqOptions(state) {
      state.isLoadingOptions = true;
    },
    gotOptions(
      state,
      action: PayloadAction<{
        campaignTypes: CampaignOption[];
        subdomains: CampaignOption[];
        triggerTypes: CampaignOption[];
      }>,
    ) {
      state.isLoadingOptions = false;
      state.campaignTypes = action.payload.campaignTypes;
      state.subdomains = action.payload.subdomains;
      state.triggerTypes = action.payload.triggerTypes;
    },
    setIsLoaded(state, action: PayloadAction<boolean>) {
      state.isLoaded = action.payload;
    },
    reqCampaign(state: CampaignDetailsState) {
      state.isLoading = true;
    },
    gotCampaign(state: CampaignDetailsState, action: PayloadAction<CampaignView>) {
      state.isLoaded = true;
      state.isLoading = false;
      state.campaign = {
        ...action.payload,
        remedialTraining: {
          ...initialState.campaign.remedialTraining,
          ...action.payload.remedialTraining,
        },
      };
    },
    errCampaign(state: CampaignDetailsState, action: PayloadAction<Error>) {
      state.isLoading = false;
      state.error = action.payload;
    },
  },
});

export const {
  saveCampaign,
  clearCampaign,
  reqOptions,
  gotOptions,
  setIsLoaded,
  reqCampaign,
  gotCampaign,
  errCampaign,
} = campaignDetailsSlice.actions;

export default campaignDetailsSlice.reducer;
